import * as Preact from 'preact';
import Box from '@mui/material/Box';
import * as ConfigBottomMenu from './A_ConfigBottomMenu';
import * as DrawerTitle from './A_DrawerTitle';
import Slide from '@mui/material/Slide';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getTheme } from './themes';
import * as ConfigProductCard from './A_ConfigProductCard';
import * as QuestionCard from './A_QuestionCard';
import * as ConfigCardGroup from './M_ConfigCardGroup';
import * as QuestionCardGroup from './M_QuestionCardGroup';

export interface Props {
    products: Array<ConfigProductCard.Props>;
    questions: Array<QuestionCard.Props>;
    title: string;
    done?: () => void;
    undo?: () => void;
    redo?: () => void;
    doneText: string;
    helperText: string;
    nullOptionText: string;
    undoText: string;
    redoText: string;
    activePage: ActivePage;
    onSelectOption: (propertyValue: string | number | null) => void;
    onOptionBecomeVisible: (propertyValue: string | number | null) => void;
    onSelectQuestion: (propertyName: string) => void;
    isSafari: boolean;
    incompatibleSelectionLoading: boolean;
    loadingText: string;
    statusText: string;
}

export enum ActivePage {
    QUESTIONS,
    OPTIONS,
}

const theme = getTheme();

/**
 * This component handles both Question Panel and Option Panel in the Figma designs, due to how the MUI Transition components work
 * it is most straightforward to combine them into one component. See https://mui.com/material-ui/guides/composition/#caveat-with-refs
 *
 * Figma design nodes:
 * Mobile: https://www.figma.com/file/va7snk88pL7TR7F75h7zbt/Design-Files?node-id=9719%3A60338 &&  https://www.figma.com/file/va7snk88pL7TR7F75h7zbt/Design-Files?node-id=9715%3A60072
 * Desktop: https://www.figma.com/file/va7snk88pL7TR7F75h7zbt/Design-Files?node-id=9719%3A60567 && https://www.figma.com/file/va7snk88pL7TR7F75h7zbt/Design-Files?node-id=9719%3A60510
 */

export const Component = (p: Props) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box
            height="100%"
            bgcolor="background.paper"
            width="100%"
            sx={{
                display: 'flex',
                paddingX: { xs: '0px', md: '16px 0px' },
                flexDirection: 'column',
                justifyContent: 'space-between',
            }}
        >
            {!isMobile && <DrawerTitle.Component title={p.title} />}
            <Box
                sx={{
                    padding: 0,
                    height: '100%',
                    overflow: 'hidden',
                }}
                position="relative"
                id="card-container"
            >
                <Box
                    height="100%"
                    sx={{
                        overflowX: { xs: 'scroll', md: 'hidden' },
                        overflowY: { xs: 'hidden', md: 'scroll' },
                    }}
                >
                    <QuestionCardGroup.Component
                        onSelectQuestion={p.onSelectQuestion}
                        nullOptionText={p.nullOptionText}
                        questions={p.questions}
                    />
                </Box>

                <Slide
                    timeout={{ enter: 500, exit: 300 }}
                    in={p.activePage === ActivePage.OPTIONS}
                    direction={isMobile ? 'up' : 'left'}
                    mountOnEnter={!p.isSafari} // we need to mount/unmount to get a smooth experience on non-safari browsers,
                    unmountOnExit={!p.isSafari} // a bug in the Slide component means the enter animation doesn't trigger if transitionDelay is also set
                    style={{ transitionDelay: p.isSafari ? '5ms' : '0ms' }} // but safari performs better with a transitiondelay and keep mounted
                >
                    <Box
                        position="absolute"
                        top={0}
                        bgcolor="background.paper"
                        height="100%"
                        width="100%"
                        sx={{
                            overflowX: { xs: 'scroll', md: 'hidden' },
                            overflowY: { xs: 'hidden', md: 'scroll' },
                        }}
                    >
                        <ConfigCardGroup.Component
                            products={p.products}
                            loading={p.incompatibleSelectionLoading}
                            statusText={p.statusText}
                            loadingText={p.loadingText}
                            helperText={p.helperText}
                            onSelectOption={p.onSelectOption}
                            onBecomeVisible={p.onOptionBecomeVisible}
                            nullOptionText={p.nullOptionText}
                        />
                    </Box>
                </Slide>
            </Box>
            <ConfigBottomMenu.Component
                undo={p.incompatibleSelectionLoading ? undefined : p.undo}
                redo={p.incompatibleSelectionLoading ? undefined : p.redo}
                doneDisabled={p.incompatibleSelectionLoading}
                done={p.done}
                undoText={p.undoText}
                redoText={p.redoText}
                doneText={p.doneText}
            />
        </Box>
    );
};
